// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideVideoPopup } from "../../store/auth/action";
import ReactPlayer from "react-player";
import { MdClose, MdPlayArrow, MdPause, MdVolumeUp, MdVolumeOff, MdFullscreen } from "react-icons/md";

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const VideoPlayerPopup = () => {
  const dispatch = useDispatch();
  const { isVidePop, videoPopupContent } = useSelector((state) => ({
    isVidePop: state.isVidePop,
    videoPopupContent: state.videoPopupContent,
  }));

  const playerRef = useRef(null);
  const playerWrapperRef = useRef(null);
  const progressBarRef = useRef(null);

  const [playing, setPlaying] = useState(true);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  if (!isVidePop) return null;

  const currentSeconds = played * duration;

  const seekTo = (fraction) => {
    playerRef.current?.seekTo(fraction, "fraction");
    setPlayed(fraction);
  };

  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current || !duration) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seekTo(fraction);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      playerWrapperRef.current?.requestFullscreen?.();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-6">
      <div className="font-geist w-[980px] max-w-full h-[580px] max-h-full bg-general-background rounded-[10px] shadow-lg outline outline-1 outline-offset-[-1px] outline-general-border flex flex-col justify-start items-start overflow-hidden">
        <div className="self-stretch px-4 py-2.5 flex flex-col justify-between items-start">
          <div className="self-stretch inline-flex justify-between items-center">
            <div className="text-general-foreground text-base font-semibold leading-6">
              {videoPopupContent?.dashboardname || videoPopupContent?.label || "Video Recording"}
            </div>
            <button type="button" onClick={() => dispatch(hideVideoPopup())} className="ani hover:opacity-70">
              <MdClose className="size-4 text-general-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="self-stretch flex-1 min-h-0 px-0.5 pb-7">
        <div
          ref={playerWrapperRef}
          className="w-full h-full bg-general-foreground relative flex flex-col justify-center items-center overflow-hidden"
        >
          <div className="absolute inset-0">
            <ReactPlayer
              ref={playerRef}
              playing={playing}
              muted={muted}
              width="100%"
              height="100%"
              url={videoPopupContent?.url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onProgress={(state) => setPlayed(state.played)}
              onDuration={(d) => setDuration(d)}
              config={{ file: { attributes: { controlsList: "nodownload" } } }}
            />
          </div>

          {!playing && (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="relative z-10 p-5 bg-general-background rounded-full flex justify-center items-center ani hover:opacity-90"
            >
              <MdPlayArrow className="size-6 text-general-foreground" />
            </button>
          )}

          <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-black/70 flex justify-start items-center gap-3">
            <button type="button" onClick={() => setPlaying((p) => !p)} className="shrink-0 ani hover:opacity-80">
              {playing ? (
                <MdPause className="size-4 text-general-background" />
              ) : (
                <MdPlayArrow className="size-4 text-general-background" />
              )}
            </button>

            <div className="shrink-0 text-white text-sm font-medium leading-5">
              {formatTime(currentSeconds)} / {formatTime(duration)}
            </div>

            <div
              ref={progressBarRef}
              onClick={handleProgressBarClick}
              className="flex-1 h-2.5 relative flex items-center cursor-pointer"
            >
              <div className="w-full h-1 absolute left-0 bg-general-background/40 rounded-xl" />
              <div
                className="h-1 absolute left-0 bg-general-primary rounded-xl"
                style={{ width: `${(played * 100).toFixed(2)}%` }}
              />
              <div
                className="size-3 absolute bg-general-background rounded-full ani"
                style={{ left: `calc(${(played * 100).toFixed(2)}% - 6px)` }}
              />
            </div>

            <button type="button" onClick={() => setMuted((m) => !m)} className="shrink-0 ani hover:opacity-80">
              {muted ? (
                <MdVolumeOff className="size-4 text-general-background" />
              ) : (
                <MdVolumeUp className="size-4 text-general-background" />
              )}
            </button>

            <button type="button" onClick={toggleFullscreen} className="shrink-0 ani hover:opacity-80">
              <MdFullscreen className="size-4 text-general-background" />
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default memo(VideoPlayerPopup);
